import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Zap,
  Mail,
  MessageSquare,
  Clock,
  Trash2,
  Play,
  Pause,
  MoreVertical
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const TRIGGERS = [
  { value: "new_lead", label: "New Lead Created", icon: Plus },
  { value: "status_change", label: "Status Changed", icon: Clock },
  { value: "time_based", label: "Time-Based", icon: Clock },
];

const ACTION_TYPES = [
  { value: "send_email", label: "Send Email", icon: Mail },
  { value: "send_sms", label: "Send SMS", icon: MessageSquare },
  { value: "wait", label: "Wait (Delay)", icon: Clock },
];

export default function AutomationPage() {
  const { user } = useContext(AuthContext);
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newAutomation, setNewAutomation] = useState({
    name: "",
    trigger: "new_lead",
    trigger_config: {},
    actions: []
  });

  useEffect(() => {
    fetchAutomations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAutomations = async () => {
    try {
      const response = await axios.get(`${API}/automations`);
      setAutomations(response.data);
    } catch (error) {
      toast.error("Failed to fetch automations");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/automations`, newAutomation);
      setAutomations([...automations, response.data]);
      setShowModal(false);
      setNewAutomation({
        name: "",
        trigger: "new_lead",
        trigger_config: {},
        actions: []
      });
      toast.success("Automation created");
    } catch (error) {
      toast.error("Failed to create automation");
    }
  };

  const handleToggle = async (automationId, isActive) => {
    try {
      await axios.put(`${API}/automations/${automationId}`, { is_active: !isActive });
      setAutomations(automations.map(a => 
        a.automation_id === automationId ? { ...a, is_active: !isActive } : a
      ));
      toast.success(isActive ? "Automation paused" : "Automation activated");
    } catch (error) {
      toast.error("Failed to update automation");
    }
  };

  const handleDelete = async (automationId) => {
    if (!window.confirm("Are you sure you want to delete this automation?")) return;
    try {
      await axios.delete(`${API}/automations/${automationId}`);
      setAutomations(automations.filter(a => a.automation_id !== automationId));
      toast.success("Automation deleted");
    } catch (error) {
      toast.error("Failed to delete automation");
    }
  };

  const addAction = () => {
    setNewAutomation({
      ...newAutomation,
      actions: [...newAutomation.actions, { type: "send_email", config: {} }]
    });
  };

  const updateAction = (index, field, value) => {
    const updatedActions = [...newAutomation.actions];
    if (field === "type") {
      updatedActions[index] = { type: value, config: {} };
    } else {
      updatedActions[index].config = { ...updatedActions[index].config, [field]: value };
    }
    setNewAutomation({ ...newAutomation, actions: updatedActions });
  };

  const removeAction = (index) => {
    setNewAutomation({
      ...newAutomation,
      actions: newAutomation.actions.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="automation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Automation
          </h1>
          <p className="text-slate-600 mt-1">Set up automated follow-ups and workflows</p>
        </div>
        <Button onClick={() => setShowModal(true)} data-testid="create-automation-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Automation
        </Button>
      </div>

      {/* Automations List */}
      {automations.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {automations.map((automation) => (
            <Card 
              key={automation.automation_id} 
              className="card-hover"
              data-testid={`automation-card-${automation.automation_id}`}
            >
              <CardHeader className="flex flex-row items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    automation.is_active ? "bg-green-100" : "bg-slate-100"
                  }`}>
                    <Zap className={`w-5 h-5 ${
                      automation.is_active ? "text-green-600" : "text-slate-400"
                    }`} />
                  </div>
                  <div>
                    <CardTitle className="text-base">{automation.name}</CardTitle>
                    <p className="text-sm text-slate-500 capitalize mt-1">
                      Trigger: {automation.trigger.replace("_", " ")}
                    </p>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem 
                      onClick={() => handleToggle(automation.automation_id, automation.is_active)}
                    >
                      {automation.is_active ? (
                        <>
                          <Pause className="w-4 h-4 mr-2" />
                          Pause
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4 mr-2" />
                          Activate
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleDelete(automation.automation_id)}
                      className="text-red-600"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {automation.actions?.map((action, index) => (
                    <div 
                      key={index} 
                      className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 rounded-lg px-3 py-2"
                    >
                      {action.type === "send_email" && <Mail className="w-4 h-4 text-blue-500" />}
                      {action.type === "send_sms" && <MessageSquare className="w-4 h-4 text-green-500" />}
                      {action.type === "wait" && <Clock className="w-4 h-4 text-yellow-500" />}
                      <span className="capitalize">{action.type.replace("_", " ")}</span>
                    </div>
                  ))}
                  {(!automation.actions || automation.actions.length === 0) && (
                    <p className="text-sm text-slate-400">No actions configured</p>
                  )}
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                  <Badge className={automation.is_active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}>
                    {automation.is_active ? "Active" : "Paused"}
                  </Badge>
                  <Switch
                    checked={automation.is_active}
                    onCheckedChange={() => handleToggle(automation.automation_id, automation.is_active)}
                    data-testid={`toggle-${automation.automation_id}`}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="py-12">
          <CardContent>
            <div className="empty-state">
              <div className="empty-state-icon">
                <Zap className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-600 font-medium">No automations yet</p>
              <p className="text-sm text-slate-400 mb-4">
                Create your first automation to streamline your follow-ups
              </p>
              <Button onClick={() => setShowModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Automation
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Automation Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Automation</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="form-field">
              <Label>Name *</Label>
              <Input
                value={newAutomation.name}
                onChange={(e) => setNewAutomation({ ...newAutomation, name: e.target.value })}
                placeholder="e.g., New Lead Welcome Sequence"
                required
                data-testid="automation-name"
              />
            </div>

            <div className="form-field">
              <Label>Trigger</Label>
              <Select
                value={newAutomation.trigger}
                onValueChange={(v) => setNewAutomation({ ...newAutomation, trigger: v })}
              >
                <SelectTrigger data-testid="automation-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRIGGERS.map((trigger) => (
                    <SelectItem key={trigger.value} value={trigger.value}>
                      {trigger.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Actions */}
            <div className="form-field">
              <div className="flex items-center justify-between mb-2">
                <Label>Actions</Label>
                <Button type="button" variant="outline" size="sm" onClick={addAction}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Action
                </Button>
              </div>
              <div className="space-y-3">
                {newAutomation.actions.map((action, index) => (
                  <div key={index} className="border border-slate-200 rounded-lg p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Action {index + 1}</span>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeAction(index)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    <Select
                      value={action.type}
                      onValueChange={(v) => updateAction(index, "type", v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ACTION_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {action.type === "send_email" && (
                      <>
                        <Input
                          placeholder="Email subject"
                          value={action.config.subject || ""}
                          onChange={(e) => updateAction(index, "subject", e.target.value)}
                        />
                        <Input
                          placeholder="Email body template"
                          value={action.config.body || ""}
                          onChange={(e) => updateAction(index, "body", e.target.value)}
                        />
                      </>
                    )}
                    
                    {action.type === "send_sms" && (
                      <Input
                        placeholder="SMS message template"
                        value={action.config.message || ""}
                        onChange={(e) => updateAction(index, "message", e.target.value)}
                      />
                    )}
                    
                    {action.type === "wait" && (
                      <Input
                        type="number"
                        placeholder="Wait duration (hours)"
                        value={action.config.hours || ""}
                        onChange={(e) => updateAction(index, "hours", parseInt(e.target.value))}
                      />
                    )}
                  </div>
                ))}
                {newAutomation.actions.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">
                    Add actions to define what happens when this automation triggers
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-automation-btn">
                Create Automation
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
